import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import { WorkScheduleEmployeesDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

interface Props {
  workSchedule: WorkSchedule
  canAssignEmployees: boolean
}

const EmployeesTable = ({ workSchedule, canAssignEmployees }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const employeesDataSource = useGraphqlDataCollectionSource({
    document: WorkScheduleEmployeesDocument,
    mapData: (data) => data.timePlanning.planningVersionsConnection.nodes,
    sortings: {
      name: {
        label: i18n.t('settings.work_schedule.employees_tab.table.header.name'),
      },
      effectiveAt: {
        label: i18n.t('settings.work_schedule.employees_tab.table.header.date'),
      },
    },
    defaultSorting: {
      field: 'name',
      order: 'asc',
    },
    search: {
      enabled: true,
    },
    mapPagination: (data) => ({
      total: data.timePlanning.planningVersionsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ search, sortings }) => ({
      search,
      scheduleId: workSchedule.id,
      sortOrder: sortings?.[0],
    }),
    itemUrl: (planningVersion) => {
      return navigation.employees.id.fullPath({ id: planningVersion.employee.id })
    },
    primaryActions: () =>
      canAssignEmployees
        ? {
            label: i18n.t('settings.work_schedule.employees_tab.add'),
            icon: Add,
            onClick: () => {
              navigate(
                navigation.settings.workSchedule.id.employees.add.fullPath({ id: workSchedule.id })
              )
            },
          }
        : undefined,
  })

  return (
    <OneDataCollection
      source={employeesDataSource}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('settings.work_schedule.employees_tab.table.header.name'),
                render: (planningVersion) => {
                  const employee = planningVersion.employee
                  return {
                    type: 'person',
                    value: {
                      firstName: employee.preferredName || employee.firstName,
                      lastName: employee.lastName,
                      src: buildUrl(employee.avatar?.url),
                    },
                  }
                },
                sorting: 'name',
              },
              {
                label: i18n.t('settings.work_schedule.employees_tab.table.header.date'),
                render: (planningVersion) => {
                  return {
                    type: 'date',
                    value: new Date(planningVersion.effectiveAt),
                  }
                },
                sorting: 'effectiveAt',
              },
            ],
          },
        },
      ]}
    />
  )
}

export default observer(EmployeesTable)
